import React, { useContext, useState } from 'react'

import { Box, Grid, Typography, Button } from '@mui/material'
import Card from 'atoms/Card'

import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

import { toast } from 'react-toastify'

import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

import { useFormDebounce } from 'hooks/useFormDebounce'
import LoadingButton from 'components/LoadingButton'

function Component401k() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  const [percentDollarsQuestion, setPercentDollarsQuestion] = useState(true)
  const [percentDollarsQuestionRoth, setPercentDollarsQuestionRoth] =
    useState(true)

  const dollarsToPercent = (contributionAmount) => {
    return intakeState?.baseSalary
      ? (((contributionAmount / 80) * 2087) / intakeState?.baseSalary) * 100
      : 0
  }
  const percentToDollars = (contributionPercent) => {
    return intakeState?.baseSalary
      ? (intakeState?.baseSalary / 2087) * 80 * (contributionPercent / 100)
      : 0
  }

  const [localForm, setLocalForm] = useState({
    traditionalBalance: intakeState.traditionalBalance || 0,
    traditionalContribution: intakeState.traditionalContribution || '',
    traditionalContributionAmount: intakeState.traditionalContributionAmount || 0,
    rothBalance: intakeState.rothBalance || 0,
    rothContribution: intakeState.rothContribution || '',
    rothContributionAmount: intakeState.rothContributionAmount || 0,
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  const showRothContribution = intakeState?.rothContribution > 0 || intakeState?.rothBalance > 0
  const showTraditionalContribution = intakeState?.traditionalContribution > 0 || intakeState?.traditionalBalance > 0

  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your deduction details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  return (
    <>
      <Box
        pl={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography display="flex" align="left" variant="body2FunHeader">
          Retirement Details
        </Typography>

        <LoadingButton loading={loading} onClick={onSubmit} />
      </Box>
      <Card>
        <Box component="form" p={3}>
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">Traditional 401k Details</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'traditional',
                label: 'Do you contribute to the Traditional 401k?',
                placeholder: '',
                inputType: 'select',
                value: intakeState.traditional,
                onChange: updateIntakeStateAndSubmit((traditional) =>
                  updateIntakeStateAtKey({ traditional })
                ),
                options: selectData.yesNo,
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'traditionalBalance',
                label: 'Current Traditional 401k balance',
                placeholder: '$250,000.00',
                inputType: 'currencyFloat',
                value: localForm.traditionalBalance,
                onChange: (e) => handleChange('traditionalBalance')(e),
                onBlur: updateIntakeStateAndSubmit((traditionalBalance) =>
                  updateIntakeStateAtKey({ traditionalBalance })
                ),
              })}
            </Grid>
            {showTraditionalContribution && (
              <>
                {percentDollarsQuestion ? (
                  <Grid item xs={12} sm={6}>
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'traditionalContribution',
                        label: 'What percentage do you contribute?',
                        placeholder: 'Ex. 5.0%',
                        inputType: 'percentage',
                        value: localForm.traditionalContributionAmount,
                        onChange: (e) => {
                          handleChange('traditionalContribution')(e)
                          handleChange('traditionalContributionAmount')(
                            percentToDollars(e)
                          )
                        },
                        onBlur: updateIntakeStateAndSubmit((traditionalContribution) =>
                          updateIntakeStateAtKey({
                            traditionalContribution,
                            traditionalContributionAmount:
                              percentToDollars(traditionalContribution),
                          })
                        ),
                      })}
                    </Grid>
                    <Box display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setPercentDollarsQuestion(false)}
                      >
                        Use amount contributed per pay period instead
                      </Button>
                    </Box>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6}>
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'traditionalContributionAmount',
                        label: 'What amount do you contribute per pay period?',
                        placeholder: '$135.00',
                        inputType: 'currencyFloat',
                        value: localForm.traditionalContributionAmount,
                        onChange: (e) => {
                          handleChange('traditionalContributionAmount')(e)
                          handleChange('traditionalContribution')(dollarsToPercent(e))
                        },
                        onBlur: updateIntakeStateAndSubmit(
                          (traditionalContributionAmount) =>
                            updateIntakeStateAtKey({
                              traditionalContributionAmount,
                              traditionalContribution: dollarsToPercent(
                                traditionalContributionAmount
                              ),
                            })
                        ),
                      })}
                    </Grid>
                    <Box display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setPercentDollarsQuestion(true)}
                      >
                        Use percentage contributed per pay period instead
                      </Button>
                    </Box>
                  </Grid>
                )}
              </>
            )}

            {/* ROTH 401k */}
            <Grid item xs={12} mt={2}>
              <Typography variant="h5">Roth 401k Details</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'roth',
                label: 'Do you contribute to the Roth 401k?',
                placeholder: '',
                inputType: 'select',
                value: intakeState.roth,
                onChange: updateIntakeStateAndSubmit((roth) =>
                  updateIntakeStateAtKey({ roth })
                ),
                options: selectData.yesNo,
              })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'rothBalance',
                label: 'Current Roth 401k balance',
                placeholder: '$250,000.00',
                inputType: 'currencyFloat',
                value: localForm.rothBalance,
                onChange: (e) => handleChange('rothBalance')(e),
                onBlur: updateIntakeStateAndSubmit((rothBalance) =>
                  updateIntakeStateAtKey({ rothBalance })
                ),
              })}
            </Grid>

            {showRothContribution && (
              <>
                {percentDollarsQuestionRoth ? (
                  <Grid item xs={12} sm={6}>
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'rothContribution',
                        label: 'What percentage do you contribute?',
                        placeholder: 'Ex. 5.0%',
                        inputType: 'percentage',
                        value: localForm.rothContributionAmount,
                        onChange: (e) => {
                          handleChange('rothContribution')(e)
                          handleChange('rothContributionAmount')(
                            percentToDollars(e)
                          )
                        },
                        onBlur: updateIntakeStateAndSubmit(
                          (tspRothContribution) =>
                            updateIntakeStateAtKey({
                              tspRothContribution,
                              tspRothContributionAmount:
                                percentToDollars(tspRothContribution),
                            })
                        ),
                      })}
                    </Grid>
                    <Box display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setPercentDollarsQuestionRoth(false)}
                      >
                        Use amount contributed per pay period instead
                      </Button>
                    </Box>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6}>
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'rothContributionAmount',
                        label: 'What amount do you contribute per pay period?',
                        placeholder: '$135.00',
                        inputType: 'currencyFloat',
                        value: localForm.rothContributionAmount,
                        onChange: (e) => {
                          handleChange('rothContributionAmount')(e)
                          handleChange('rothContribution')(
                            dollarsToPercent(e)
                          )
                        },
                        onBlur: updateIntakeStateAndSubmit(
                          (rothContributionAmount) =>
                            updateIntakeStateAtKey({
                              rothContributionAmount,
                              rothContribution: dollarsToPercent(
                                rothContributionAmount
                              ),
                            })
                        ),
                      })}
                    </Grid>
                    <Box display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setPercentDollarsQuestionRoth(true)}
                      >
                        Use percentage contributed per pay period instead
                      </Button>
                    </Box>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Box>
      </Card>
    </>
  )
}

export default React.memo(Component401k)
